<template>
    <checkout-step-two-layout>
        <template v-slot:header>
            <div class="checkout__cart--flex" :class="{ 'checkout__cart--flex-rtl': isRTL }">
                <div class="checkout__cart--flex-img">
                    <img
                        src="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'></svg>"
                        :data-src="product.checkout_graphic"
                        :alt="product.title"
                        class="lazy" />
                </div>
                <div class="checkout__cart--flex-text">
                    <p>
                        <span
                            @click="closeStepTwo"
                            class="checkout__close checkout__close--step-2"
                            data-test="checkout-step-2-close"
                            >&times;</span
                        >
                        <span v-if="!isLifetimeUpdates" class="checkout__cart--old-price">{{ originalPrice }}</span>
                        <span
                            v-if="product.price.has_lowest_before && !removeLowestBeforePrice"
                            class="checkout__cart--old-price">
                            &nbsp;{{ lowestBeforePrice }}
                        </span>
                        <transition name="fade" mode="out-in">
                            <span v-if="paymentType === 'now'" key="now" class="checkout__cart--pay-now-price">&nbsp;
                                <product-price type="hard" :field="priceField"></product-price>
                            </span>
                        </transition>
                        <transition name="fade" mode="out-in">
                            <span v-if="paymentType === 'plan'" key="plan" class="checkout__cart--plan-price"
                                >&nbsp;3 x <product-price type="hard" field="plan"></product-price
                            ></span>
                        </transition>
                        <br />
                        <span v-html="product.title"></span>
                        <span
                            class="checkout__gift"
                            v-if="isGift"
                            v-html="`+ ${$t('checkout.freeCourse')} iPhone Food Photography`">
                        </span>
                    </p>
                </div>
            </div>
        </template>

        <template v-slot:content v-if="!isLifetimeUpdates">
            <div class="checkout__payment_type" :class="{ 'checkout__payment_type--rtl': isRTL }">
                <div v-if="product.price.has_plan">
                    <input
                        type="radio"
                        id="one-time"
                        value="now"
                        v-model="paymentType"
                        data-test="one-time-radio-button" />
                    <label for="one-time"
                        >{{ $t('checkout.onePayment') }} <product-price field="full"></product-price
                    ></label>
                    <template v-if="!hidePaymentPlan">
                        <input type="radio" id="plan" value="plan" v-model="paymentType" />
                        <label for="plan" data-test="plan-radio-button"
                            >{{ $t('checkout.planPayment') }} <product-price field="plan"></product-price
                        ></label>
                    </template>
                </div>
            </div>
        </template>

        <template v-slot:paypal>
            <paypal-payment
                :braintree-client-instance="braintreeClientInstance"
                @received-token="submitPayment"></paypal-payment>
        </template>

        <template v-slot:apple-pay>
            <apple-pay-payment
                v-if="applePayEnvironmentAvailable"
                v-show="paymentType === 'now'"
                :braintree-client-instance="braintreeClientInstance"
                @received-token="submitPayment"></apple-pay-payment>
        </template>

        <template v-slot:credit-card>
            <credit-card-payment
                :braintree-client-instance="braintreeClientInstance"
                :submit-title="submitTitle"
                @received-token="submitPayment"
                :is-lifetime-updates="isLifetimeUpdates"></credit-card-payment>
        </template>
    </checkout-step-two-layout>
</template>

<script>
import braintree from 'braintree-web';
import { mapGetters, mapState } from 'vuex';
import axios from 'axios';
import CheckoutStepTwoLayout from './CheckoutStepTwoLayout.vue';
import CreditCardPayment from './payment_handlers/CreditCardPayment.vue';
import PaypalPayment from './payment_handlers/PaypalPayment.vue';
import ApplePayPayment from './payment_handlers/ApplePayPayment.vue';
import msg from '../../messages';

export default {
    components: {
        CreditCardPayment,
        PaypalPayment,
        ApplePayPayment,
        CheckoutStepTwoLayout,
    },

    props: {
        standalone: {
            type: Boolean,
            required: false,
            default: false,
        },
        isLifetimeUpdates: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    data: function () {
        return {
            braintree_client_token: null,
            braintreeClientInstance: null,
            set_up_braintree_client_on_token_load: false,
            failed_fetch_price_call_count: 0,
            failed_bt_client_token_call_count: 0,
            failed_bt_client_create_count: 0,
            initial_bt_client_setup: true,
            braintree_setup_started: false,
        };
    },

    computed: {
        ...mapGetters(['product']),
        paymentType: {
            get() {
                return this.$store.state.paymentType;
            },
            set(value) {
                this.$store.commit('setPaymentType', value);
                if (this.applePayAvailable && value === 'now') {
                    this.eventBus.emit('MAKE_IMMEDIATE_APPLE_PAY_PAYMENT');
                }
            },
        },
        ...mapState([
            'popup',
            'salesButtonVisible',
            'emailInput',
            'currency',
            'initialCurrency',
            'sessionId',
            'originalUrl',
            'originalDate',
            'removeOriginalPrice',
            'removeLowestBeforePrice',
            'defaultPaymentMethodToken',
            'hidePaymentPlan',
            'applePayAvailable',
        ]),
        isRTL() {
            return window.App.isRTL;
        },
        stepTwoOpen() {
            return this.popup.isOpen && this.popup.step === 2;
        },
        applePayEnvironmentAvailable() {
            return location.protocol == 'https:' && window.ApplePaySession && ApplePaySession.canMakePayments();
        },
        originalPrice() {
            return this.removeOriginalPrice
                ? null
                : this.product.price.original
                  ? this.product.price.original.currency_symbol + this.product.price.original.value.toLocaleString()
                  : null;
        },
        lowestBeforePrice() {
            return this.product.price.lowest_before
                ? this.product.price.lowest_before.currency_symbol + this.product.price.lowest_before.value
                : null;
        },
        title() {
            if (this.product.slug === 'iphone-food-photography') {
                return 'The Food Course';
            } else {
                return this.product.title;
            }
        },
        productTitle() {
            return this.product.title;
        },
        currentLanguage() {
            return window.App.language;
        },
        submitTitle() {
            const isGerman = this.currentLanguage === 'de';

            if (this.product.slug === 'signature-preset-collection'|| this.isLifetimeUpdates) {
                return this.$t('checkout.getInstantAccess');
            } else {
                return isGerman
                    ? `${this.title} ${this.$t('checkout.submitTitle')}`
                    : `${this.$t('checkout.submitTitle')} ${this.title}`;
            }
        },
        isGift() {
            return window.App.isGift;
        },
        priceField() {
            return this.isLifetimeUpdates ? 'lu' : 'full';
        },
    },

    watch: {
        // Only set up braintree instance when checkout popup is opened
        'popup.isOpen': 'setupBraintreeClient',
        salesButtonVisible: 'setupBraintreeClient',
    },

    mounted() {
        if (!this.standalone) {
            this.fetchPrices();
        }
        this.eventBus.on('SETUP_BRAINTREE_CLIENT', () => {
            this.setupBraintreeClient();
        });
    },

    methods: {
        handleLowestBefore(visible) {
            if (visible) {
                const lowestBefore = document.querySelector('.old_price.lowest_before');

                if (!lowestBefore) return;

                lowestBefore.style.display = 'block';
            }
        },
        fetchPrices() {
            logger.addCheckoutBreadcrumb({
                message: msg.checkout.fetchPricesStarted,
                data: { attempt: this.failed_fetch_price_call_count },
            });

            return axios
                .post(
                    '/api/fetch-prices',
                    {
                        currency: this.initialCurrency,
                        psh: this.sessionId,
                        original_url: this.originalUrl,
                        original_date: this.originalDate,
                    },
                    {
                        headers: {
                            'x-transaction-id': logger.getTransactionId(),
                        },
                    },
                )
                .then((response) => {
                    if (!response.data.currency || !response.data.product) {
                        return this.retryFetchPrices();
                    }
                    this.$store.commit('setCurrency', {
                        currency: response.data.currency,
                        enforceHardDisplay: response.data.enforce_hard_display,
                    });
                    this.$store.commit('setProduct', response.data.product);
                    this.$store.commit('setIsSca', response.data.is_sca);

                    this.handleLowestBefore(response.data.product.price.has_lowest_before);

                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchPricesCompleted,
                        data: {
                            attempt: this.failed_fetch_price_call_count,
                            currency: response.data.currency,
                            product: response.data.product,
                        },
                    });

                    // Give access of currency to support form
                    window.support_currency = response.data.currency;
                })
                .catch((err) => {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchPricesTryFailed,
                        data: {
                            attempt: this.failed_fetch_price_call_count,
                            err,
                        },
                    });

                    this.retryFetchPrices();
                });
        },

        retryFetchPrices() {
            if (++this.failed_fetch_price_call_count < 5) {
                setTimeout(() => {
                    this.fetchPrices();
                }, 3000);
            } else {
                logger.captureException(new Error(msg.checkout.fetchPricesFailed));
            }
        },
        async setupBraintreeClient() {
            if (!this.currency) {
                // we need currency for braintree client token otherwise 3DS isn't working
                setTimeout(this.setupBraintreeClient, 10);
                return;
            }

            if (this.braintree_setup_started) {
                // If page is refreshed while scrolled to button multiple salesButtonVisible
                // events are fired causing multiple PayPal button creation
                return;
            }

            if (this.initial_bt_client_setup) {
                // Added this so we only show this breadcrumb once.
                this.initial_bt_client_setup = false;

                logger.addCheckoutBreadcrumb({ message: msg.checkout.createBtClientStarted });
            }

            this.braintree_setup_started = true;

            if (!this.braintree_client_token) {
                try {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchAsyncBtTokenStarted,
                        data: { attempt: this.failed_bt_client_token_call_count + 1 },
                    });

                    let braintreeClientTokenResponse = await axios.post('/api/async-braintree-token', {
                        currency: this.currency,
                    });

                    this.braintree_client_token = braintreeClientTokenResponse.data.braintree_client_token;

                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchAsyncBtTokenCompleted,
                        data: { attempt: this.failed_bt_client_token_call_count + 1 },
                    });
                } catch (err) {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.fetchAsyncBtTokenTryFailed,
                        data: {
                            attempt: this.failed_bt_client_token_call_count + 1,
                            err,
                        },
                    });

                    this.retrySetupBraintreeToken();
                    return;
                }
            }

            if (!this.braintreeClientInstance) {
                try {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.createBtClientTryStarted,
                        data: { attempt: this.failed_bt_client_create_count + 1 },
                    });

                    this.braintreeClientInstance = await braintree.client.create({
                        authorization: this.braintree_client_token,
                    });

                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.createBtClientTryCompleted,
                        data: { attempt: this.failed_bt_client_create_count },
                    });
                } catch (err) {
                    logger.addCheckoutBreadcrumb({
                        message: msg.checkout.createBtClientTryFailed,
                        data: {
                            attempt: this.failed_bt_client_create_count + 1,
                            err,
                        },
                    });

                    this.retrySetupBraintreeClient();
                    return;
                }
            }

            this.setUpDataCollector();

            logger.addCheckoutBreadcrumb({ message: msg.checkout.createBtClientCompleted });
        },
        retrySetupBraintreeClient() {
            if (++this.failed_bt_client_create_count < 3) {
                setTimeout(() => {
                    this.braintree_setup_started = false;
                    this.setupBraintreeClient();
                }, 1500);
            } else {
                logger.captureException(new Error(msg.checkout.createBtClientFailed));
            }
        },
        retrySetupBraintreeToken() {
            if (++this.failed_bt_client_token_call_count < 5) {
                setTimeout(() => {
                    this.braintree_setup_started = false;
                    this.setupBraintreeClient();
                }, 1500);
            } else {
                logger.captureException(new Error(msg.checkout.fetchAsyncBtTokenFailed));
            }
        },
        fbLogOut() {
            this.$store.commit('clearFbUserInfo');
            this.$store.commit('setEmailInput', '');
            // Disable "first autofocus" hack whan FB logout happens to keep design looking neat
            this.eventBus.emit('TEMPORARY_DISABLE_FIRST_AUTOFOCUS');

            this.$store.commit('openCheckout', {
                step: 1,
            });
        },
        closeStepTwo() {
            this.$store.commit('closeCheckout');
        },
        submitPayment(token) {
            this.$store.dispatch('checkoutPayment', token);
        },
        setUpDataCollector() {
            braintree.dataCollector
                .create({
                    client: this.braintreeClientInstance,
                })
                .then((collectorInstance) => {
                    this.$store.commit('setBraintreeDeviceData', collectorInstance.deviceData);
                })
                .catch((err) => {
                    logger.captureException(err);
                });
        },
    },
};
</script>
